import { useState, useEffect, useMemo } from 'react';
import { Modal, Form, Button, Row, Col, Alert, Image } from 'react-bootstrap';
import type { Review } from '../types';

interface ReviewDetailModalProps {
  show: boolean;
  review: Review | null;
  onClose: () => void;
  onSave: (review: Review) => void;
  isUpdating?: boolean;
}

export const ReviewDetailModal = ({ show, review, onClose, onSave, isUpdating = false }: ReviewDetailModalProps) => {
  const [editedReview, setEditedReview] = useState<Review | null>(null);

  useEffect(() => {
    if (review) {
      setEditedReview({ ...review });
    }
  }, [review]);

  const isModified = useMemo(() => {
    if (!review || !editedReview) {
      return false;
    }
    return JSON.stringify(review) !== JSON.stringify(editedReview);
  }, [review, editedReview]);

  const handleSave = () => {
    if (editedReview && !isUpdating) {
      onSave(editedReview);
    }
  };

  const handleCancel = () => {
    setEditedReview(review ? { ...review } : null);
    onClose();
  };

  if (!editedReview) {
    return null;
  }

  return (
    <Modal show={show} onHide={handleCancel} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Review Details - ID: {editedReview.reviewId}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isModified && (
          <Alert variant="info" className="mb-3">
            You have unsaved changes.
          </Alert>
        )}

        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Product ID</Form.Label>
              <Form.Control
                type="number"
                value={editedReview.productId}
                onChange={(e) => setEditedReview({ ...editedReview, productId: parseInt(e.target.value) })}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={editedReview.status}
                onChange={(e) => setEditedReview({ ...editedReview, status: e.target.value })}
              >
                <option value="Submitted">Submitted</option>
                <option value="Curated">Curated</option>
                <option value="Published">Published</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Reviewer Name</Form.Label>
              <Form.Control
                type="text"
                value={editedReview.name}
                onChange={(e) => setEditedReview({ ...editedReview, name: e.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={editedReview.email}
                onChange={(e) => setEditedReview({ ...editedReview, email: e.target.value })}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Rating</Form.Label>
              <Form.Select
                value={editedReview.rating}
                onChange={(e) => setEditedReview({ ...editedReview, rating: parseInt(e.target.value) })}
              >
                <option value={1}>1 Star</option>
                <option value={2}>2 Stars</option>
                <option value={3}>3 Stars</option>
                <option value={4}>4 Stars</option>
                <option value={5}>5 Stars</option>
              </Form.Select>
              <div className="mt-2">
                {[...Array(editedReview.rating)].map((_, i) => (
                  <span key={i} className="text-warning">
                    ★
                  </span>
                ))}
                <span className="text-muted"> ({editedReview.rating}/5)</span>
              </div>
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3">
          <Form.Label>Review Title</Form.Label>
          <Form.Control
            type="text"
            value={editedReview.title}
            onChange={(e) => setEditedReview({ ...editedReview, title: e.target.value })}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Review Content</Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            value={editedReview.body}
            onChange={(e) => setEditedReview({ ...editedReview, body: e.target.value })}
          />
        </Form.Group>

        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Skin Type</Form.Label>
              <Form.Control
                type="text"
                value={editedReview.skinType.join(', ')}
                onChange={(e) =>
                  setEditedReview({
                    ...editedReview,
                    skinType: e.target.value
                      .split(',')
                      .map((s) => s.trim())
                      .filter((s) => s.length > 0),
                  })
                }
                placeholder="e.g., Combination, Dry, Oily"
              />
              <Form.Text className="text-muted">Separate multiple values with commas</Form.Text>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Skin Concern</Form.Label>
              <Form.Control
                type="text"
                value={editedReview.skinConcern.join(', ')}
                onChange={(e) =>
                  setEditedReview({
                    ...editedReview,
                    skinConcern: e.target.value
                      .split(',')
                      .map((s) => s.trim())
                      .filter((s) => s.length > 0),
                  })
                }
                placeholder="e.g., Acne, Dryness, Aging"
              />
              <Form.Text className="text-muted">Separate multiple values with commas</Form.Text>
            </Form.Group>
          </Col>
        </Row>

        {editedReview.pictureUrls.length > 0 && (
          <Form.Group className="mb-3">
            <Form.Label>Review Images</Form.Label>
            <Row>
              {editedReview.pictureUrls.map((url, index) => (
                <Col key={index} xs={6} md={3} className="mb-2">
                  <Image
                    src={url}
                    alt={`Review image ${index + 1}`}
                    thumbnail
                    style={{ width: '100%', height: '100px', objectFit: 'cover' }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'https://via.placeholder.com/100x100?text=No+Image';
                    }}
                  />
                </Col>
              ))}
            </Row>
          </Form.Group>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleCancel} disabled={isUpdating}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={!isModified || isUpdating}>
          {isUpdating ? 'Saving...' : 'Save Changes'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
